import { Alert, LoadingButton } from '@mui/lab';

import { AccessFlowFormData } from '@AccessFlows/common/types';
import { useState } from 'react';
import { useAccessFlowsPack } from '@hooks';

import { AccessFlowForm } from '@AccessFlows/organisms/AccessFlowForm/AccessFlowForm';
import { useAppCreateAccessFlow, useAppCreateAccessFlowV2 } from '@AccessFlows/services/accessFlowsMutations';
import { Loader } from '@components';
import { useAppGetAccessFlowRemediation } from '@StandingAccess/services/standingAccessQueries';
import { UserRequestSummary } from '@AccessFlows/organisms/FlowForm/Summary';
import { Box, Button } from '@mui/material';
import { RouterLink } from '@libs';

interface AssessmentReportAccessFlowFormProps {
  reportId: string;
  tier: string;
  donePath?: string;
}

function useAssessmentReportAccessFlowForm({ reportId, tier }: AssessmentReportAccessFlowFormProps) {
  const { isFetched: isPackFetched } = useAccessFlowsPack();
  const { accessFlowRemediation, isFetched: isAccessFlowRemediationFetched } = useAppGetAccessFlowRemediation({
    reportId,
    tier,
  });

  const isFetched = isPackFetched && isAccessFlowRemediationFetched;

  const initialData = accessFlowRemediation
    ? {
        ...accessFlowRemediation,
        grantees: undefined,
        granteesV2: accessFlowRemediation.grantees,
      }
    : undefined;

  return {
    initialData,
    isFetched,
  };
}

export default function AssessmentReportAccessFlowForm({
  reportId,
  tier,
  donePath,
}: AssessmentReportAccessFlowFormProps) {
  const { initialData, isFetched } = useAssessmentReportAccessFlowForm({
    reportId,
    tier,
    donePath,
  });

  if (!isFetched) {
    return <Loader />;
  }

  if (!initialData) {
    return <Alert severity="error">No remediation data found</Alert>;
  }

  return <AssessmentReportAccessFlowFormContainer initialData={initialData} donePath={donePath} />;
}

interface AssessmentReportAccessFlowFormContainerProps {
  initialData: AccessFlowFormData;
  donePath?: string;
}

function useAssessmentReportAccessFlowFormContainer({ initialData }: AssessmentReportAccessFlowFormContainerProps) {
  const [formData, setFormData] = useState<AccessFlowFormData>(initialData);

  const { createAccessFlow, isLoading: isCreateAccessFlowLoading, createdAccessFlow } = useAppCreateAccessFlow();
  const {
    createAccessFlow: createAccessFlowV2,
    isLoading: isCreateAccessFlowLoadingV2,
    createdAccessFlow: createdAccessFlowV2,
  } = useAppCreateAccessFlowV2();

  const handleChange = (data: AccessFlowFormData) => setFormData(data);

  const isSubmitting = isCreateAccessFlowLoading || isCreateAccessFlowLoadingV2;

  return {
    formData,
    isSubmitting,
    handleChange,
    handleSubmit: createAccessFlow,
    handleSubmitV2: createAccessFlowV2,
    createdAccessFlow: createdAccessFlow || createdAccessFlowV2,
  };
}

function AssessmentReportAccessFlowFormContainer({
  initialData,
  donePath,
}: AssessmentReportAccessFlowFormContainerProps) {
  const { formData, isSubmitting, handleChange, handleSubmit, handleSubmitV2, createdAccessFlow } =
    useAssessmentReportAccessFlowFormContainer({ initialData });

  if (createdAccessFlow && donePath) {
    return (
      <Box display="flex" width="100%">
        <UserRequestSummary accessFlowName={createdAccessFlow.name}>
          <Button variant="contained" component={RouterLink} to={donePath}>
            Done
          </Button>
        </UserRequestSummary>
      </Box>
    );
  }

  return (
    <AccessFlowForm
      forcedTriggerType
      formData={formData}
      onChange={handleChange}
      onSubmit={handleSubmit}
      onSubmitV2={handleSubmitV2}
    >
      <LoadingButton data-testid="create-access-flow-btn" variant="contained" type="submit" loading={isSubmitting}>
        Create Access Flow
      </LoadingButton>
    </AccessFlowForm>
  );
}
